import { create, fromJson, type JsonValue, toJson } from "@bufbuild/protobuf";
import { type Value, ValueSchema } from "@bufbuild/protobuf/wkt";

import { apiClient } from './request';
import { fetchWithApiMock } from "./mock";
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { RAGCollection } from '@/proto/agentplatform/v1/rag_pb';
import {
  ListRAGCollectionsResponseSchema,
  CreateRAGCollectionRequestSchema,
  CreateRAGCollectionResponseSchema,
  DeleteRAGCollectionRequestSchema,
  DeleteRAGCollectionResponseSchema,
  ListRAGIngestJobsResponseSchema,
  DeleteRAGIngestJobRequestSchema,
  DeleteRAGIngestJobResponseSchema,
  BatchDeleteRAGIngestJobsRequestSchema,
  BatchDeleteRAGIngestJobsResponseSchema,
  RerunRAGIngestJobRequestSchema,
  RerunRAGIngestJobResponseSchema,
  UploadRAGPDFsResponseSchema,
  UploadRAGFeishuRequestSchema,
  UploadRAGFeishuResponseSchema,
  UploadRAGWebRequestSchema,
  UploadRAGWebResponseSchema,
  ParseRAGSitemapRequestSchema,
  ParseRAGSitemapResponseSchema,
  GetRAGIngestJobRequestSchema,
  GetRAGIngestJobResponseSchema,
  GetRAGIngestJobLogsRequestSchema,
  GetRAGIngestJobLogsResponseSchema,
  ListRAGRerunJobsResponseSchema,
  DeleteRAGRerunJobRequestSchema,
  DeleteRAGRerunJobResponseSchema,
} from "@/proto/agentplatform/httpv2/rag_pb";

/**
 * v2 透传响应的 `data` 字段是 `google.protobuf.Value` 包装，需经 protojson 编解码
 * 还原为普通 JS 值后再按既有接口断言（避免手写 JSON 绕过 proto codec）。
 */
function decodeValueData(v: Value | undefined): JsonValue {
  return v ? toJson(ValueSchema, v) : {};
}
import { getBearerToken } from "@/utils/authToken";

/** Same origin base as `apiClient` so `fetch` log streams match axios paths (incl. `/api` vs `/api/v1`). */
function apiBaseForBrowserFetch(): string {
  const base = (apiClient.defaults.baseURL as string | undefined)?.trim();
  return base && base.length > 0 ? base : "/api";
}

function absoluteApiUrl(path: string): string {
  const base = apiBaseForBrowserFetch().replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

/** Matches backend pdfJobResult / RAG ingest rag_ingest_jobs row (via proxy) */
export interface RagPdfJobResult {
  /** Row id from rag_ingest_jobs when loaded from history */
  id?: string;
  original_filename: string;
  ok: boolean;
  job_id?: string;
  status?: string;
  created_at?: string;
  status_path?: string;
  logs_path?: string;
  logs_stream_path?: string;
  error?: string;
  /** Present when loaded from ingest history list */
  qdrant_collection_name?: string;
  /** Human-readable collection title from `rag_collections.name`; omitted when unregistered / legacy */
  collection_display_name?: string;
  /** File content MD5 hex; multi-file list rows may use first of doc_ids */
  doc_id?: string;
  /** Ingest task name from upstream rag_ingest_jobs.name (e.g. "sitemap:https://...") */
  name?: string;
}

/** Single row from GET /rag/rag-ingest-jobs (proxied from RAG ingest GET /api/v1/ingestion/rag-jobs) */
export interface RagIngestJobRow {
  id: string;
  name?: string;
  original_filename: string;
  rag_job_id: string;
  qdrant_collection_name: string;
  /** Registered collection display name (from `rag_collections`), for UI */
  collection_display_name?: string;
  job_status: string;
  error_message: string;
  /** Single doc_id when upstream exposes one */
  doc_id?: string;
  /** Multiple doc_ids when batch ingest */
  doc_ids?: string[];
  created_at: string;
  ok: boolean;
}

export interface ListRagIngestJobsResponse {
  total: number;
  jobs: RagIngestJobRow[];
}

export interface RagRerunIngestJobResponse {
  job_id: string;
  status: string;
  created_at?: string;
  status_url?: string;
  logs_url?: string;
  logs_stream_url?: string;
  rag_ingest_jobs_id?: string;
  rerun_kind?: string;
  qdrant_cleanup_skipped?: boolean;
  qdrant_cleanup_note?: string;
  /**
   * 该知识库的 collection 不是蓝绿 alias，本次重跑退回了普通导入。
   * - 'qdrant_alias_conflict_cleaned'：退回前已清掉旧向量，内容不会重复；
   * - 'qdrant_alias_conflict_dirty'：上游记录已丢失、无法清理，本次导入会产生重复内容。
   */
  blue_green_fallback?:
    "qdrant_alias_conflict_cleaned" | "qdrant_alias_conflict_dirty";
}

export function ragIngestRowToResult(row: RagIngestJobRow): RagPdfJobResult {
  const firstDoc =
    row.doc_ids && row.doc_ids.length > 0 ? row.doc_ids[0] : row.doc_id;
  return {
    id: row.id,
    original_filename: row.original_filename,
    ok: row.ok,
    job_id: row.rag_job_id || undefined,
    status: row.job_status || undefined,
    error: row.error_message || undefined,
    created_at: row.created_at,
    qdrant_collection_name: row.qdrant_collection_name,
    collection_display_name: row.collection_display_name?.trim() || undefined,
    doc_id: firstDoc?.trim() || undefined,
    name: row.name?.trim() || undefined,
  };
}

export interface RagUploadResponse {
  results: RagPdfJobResult[];
}

export interface RagFeishuUploadResponse {
  job_id: string;
  status: string;
  created_at?: string;
  status_url?: string;
  logs_url?: string;
  logs_stream_url?: string;
  rag_ingest_jobs_id?: string;
  feishu_document_id?: string;
}

export interface RagWebUploadResponse {
  job_id: string;
  status: string;
  created_at?: string;
  status_url?: string;
  logs_url?: string;
  logs_stream_url?: string;
  rag_ingest_jobs_id?: string;
  urls?: string[];
  sitemap_url?: string;
  /** When auto_rerun_interval_hours is specified, this is the ID of the created RAG_RERUN cron job */
  auto_rerun_job_id?: string;
}

/** A RAG_RERUN cron job from GET /rag/rag-rerun-jobs */
export interface RagRerunJob {
  id: string;
  name: string;
  cron: string;
  /** "sitemap" or "web" */
  kind: string;
  /** Qdrant physical collection name (AGENT_RAG_rc-xxx) */
  collection_id: string;
  /** Human-readable collection name from job spec */
  collection_display_name?: string;
  /** For sitemap kind, the sitemap URL */
  sitemap_url?: string;
  /** For web kind, the list of URLs to crawl */
  urls?: string[];
  status: string;
  next_fire_at?: string;
  last_fired_at?: string;
  /** Latest rag ingest job id (jo-…) from the most recent cron fire */
  last_ingest_job_id?: string;
  /** Status of the most recent cron run: JOB_RUN_STATUS_{RUNNING,COMPLETED,FAILED} */
  last_run_status?: string;
  /** Human-readable result of the most recent cron run (set when it completed) */
  last_run_result?: string;
  /** Error message of the most recent cron run (set when it failed) */
  last_run_error?: string;
  last_run_finished_at?: string;
  created_at: string;
}

export interface ListRagRerunJobsResponse {
  total: number;
  jobs: RagRerunJob[];
}

export interface SitemapParseResponse {
  urls: string[];
  total: number;
  unreachable?: number;
  original_total?: number;
  truncated?: boolean;
  truncation_msg?: string;
}

export interface RagJobStatusResponse {
  job_id?: string;
  status?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
  duration?: unknown;
  exit_code?: unknown;
  error?: unknown;
}

export interface RagJobLogsResponse {
  job_id?: string;
  status?: string;
  stdout?: string;
  stderr?: string;
}

const MAX_PDFS = 5;
/** 与 RAG 上传页文案、前端校验一致；超过则拒绝上传 */
const MAX_PDF_PAGES = 200;

/**
 * Normalize raw subprocess/tqdm output for <pre> display:
 * strip ANSI CSI/OSC (avoids stray "[A" fragments), unify CR/LF, omit blank lines.
 */
export function normalizeStreamLogText(full: string): string {
  let s = full;
  // CSI (colors, cursor moves — tqdm uses these with \r redraw)
  // eslint-disable-next-line no-control-regex -- CSI sequences intentionally match control chars
  s = s.replace(/\u001b\[[\x30-\x3f]*[\x20-\x2f]*[\x40-\x7e]/g, "");
  // OSC sequences (e.g. window title)
  // eslint-disable-next-line no-control-regex -- OSC sequences intentionally match control chars
  s = s.replace(/\u001b\][^\u0007]*\u0007/g, "");
  s = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  return s
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .join("\n");
}

/** Hide PDF MD5 / vector doc_id hex in logs shown to users (32-char hex tokens). */
export function redactDocIdsInLogDisplay(text: string): string {
  return text.replace(/\b[a-fA-F0-9]{32}\b/g, "[…]");
}

const RAG_QDRANT_PREFIX = "AGENT_RAG_";

/** Derive the Qdrant physical collection name from a rag_collections resource ID. */
function ragQdrantCollectionName(id: string): string {
  if (!id) return "";
  if (id.startsWith(RAG_QDRANT_PREFIX)) return id;
  return RAG_QDRANT_PREFIX + id;
}

export interface RagCollectionRow extends Partial<Pick<RAGCollection, 'embeddingProfileId'>> {
  id: string;
  collection_display_name: string;
  collection_name: string;
  /** Qdrant physical collection name (AGENT_RAG_rc-xxx); derived from id, not stored in DB. */
  qdrant_collection_name: string;
  /** 用户填写的 collection 说明 */
  description?: string;
  /** rag 向量维度；未设置时由上游默认 */
  vector_size?: number;
  /** rag 分块重叠；未设置时由上游默认 */
  chunk_overlap?: number;
  /** rag Qdrant 距离度量（cosine / euclid / dot）；未设置时由上游默认 */
  distance?: string;
  created_at: string;
  author?: string;
  status?: number;
  /** 登记人：Valhalla 登录名（User.Name），无则 IAM id */
  owner_user_id?: string;
}

export interface ListRagCollectionsResponse {
  total: number;
  collections: RagCollectionRow[];
}

/** Map proto JSON (camelCase) from rag collection APIs to UI row shape. */
function mapProtoRagCollection(row: Partial<RAGCollection>): RagCollectionRow {
  const createdRaw = row.createdAt ?? 0;
  const createdNs =
    typeof createdRaw === "bigint"
      ? createdRaw
      : BigInt(
          typeof createdRaw === "string"
            ? createdRaw
            : Math.trunc(Number(createdRaw)),
        );
  const createdAt =
    createdNs > 0n
      ? new Date(Number(createdNs / 1_000_000n)).toISOString()
      : new Date(0).toISOString();
  const vectorSize = row.vectorSize;
  const chunkOverlap = row.chunkOverlap;
  const userId = (row.userId ?? '').trim();
  return {
    id: row.id ?? "",
    collection_display_name: row.name ?? "",
    collection_name: row.id ?? "",
    qdrant_collection_name: ragQdrantCollectionName(row.id ?? ""),
    description: row.description?.trim() || undefined,
    author: row.author?.trim() || undefined,
    owner_user_id: userId || undefined,
    status: row.status,
    created_at: createdAt,
    vector_size: vectorSize && vectorSize > 0 ? vectorSize : undefined,
    chunk_overlap: chunkOverlap && chunkOverlap > 0 ? chunkOverlap : undefined,
    distance: row.distance?.trim() || undefined,
    embeddingProfileId: row.embeddingProfileId ?? '',
  };
}

export interface CreateRagCollectionResponse extends Partial<
  Pick<RAGCollection, 'embeddingProfileId'>
> {
  id: string;
  collection_display_name: string;
  collection_name: string;
  qdrant_collection_name: string;
  description?: string;
  vector_size?: number;
  chunk_overlap?: number;
  distance?: string;
  created_at: string;
  /** 登记人：Valhalla 登录名（User.Name），无则 IAM id */
  owner_user_id?: string;
}

export type RagCollectionDistance = "cosine" | "euclid" | "dot";

export const ragApi = {
  maxPdfs: MAX_PDFS,
  maxPdfPages: MAX_PDF_PAGES,

  /** Current user's registered Qdrant collection names (tenant-scoped). */
  listRagCollections: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ListRagCollectionsResponse> => {
    const req = create(ListRequestSchema, {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 100,
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/ListRAGCollections",
      toJson(ListRequestSchema, req),
    );
    const resp = fromJson(
      ListRAGCollectionsResponseSchema,
      responseObject.data,
    );
    const collections = (resp.data?.items ?? []).map(mapProtoRagCollection);
    return { total: resp.data?.total ?? collections.length, collections };
  },

  /** Create a Qdrant collection; display name is user-facing, collection_name is generated server-side. */
  createRagCollection: async (params: {
    collection_display_name: string;
    description?: string;
    vector_size?: number;
    chunk_overlap?: number;
    distance?: string;
  }): Promise<CreateRagCollectionResponse> => {
    const req = create(CreateRAGCollectionRequestSchema, {
      collectionDisplayName: params.collection_display_name,
      description: params.description ?? "",
      vectorSize: params.vector_size ?? 0,
      chunkOverlap: params.chunk_overlap ?? 0,
      distance: params.distance ?? "",
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/CreateRAGCollection",
      toJson(CreateRAGCollectionRequestSchema, req),
    );
    const resp = fromJson(
      CreateRAGCollectionResponseSchema,
      responseObject.data,
    );
    return mapProtoRagCollection(resp.data ?? {});
  },

  /** Remove platform registration and drop the Qdrant collection via rag (owner only). */
  deleteRagCollection: async (
    id: string,
  ): Promise<{
    deleted: boolean;
    collection_name: string;
    qdrant_deleted: boolean;
  }> => {
    const req = create(DeleteRAGCollectionRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/DeleteRAGCollection",
      toJson(DeleteRAGCollectionRequestSchema, req),
    );
    const resp = fromJson(
      DeleteRAGCollectionResponseSchema,
      responseObject.data,
    );
    const data = decodeValueData(resp.data) as {
      deleted?: boolean;
      collectionName?: string;
      collection_name?: string;
      qdrantDeleted?: boolean;
      qdrant_deleted?: boolean;
    };
    return {
      deleted: data.deleted ?? false,
      collection_name: data.collectionName ?? data.collection_name ?? id,
      qdrant_deleted: data.qdrantDeleted ?? data.qdrant_deleted ?? false,
    };
  },

  /** Persisted build jobs for current tenant (survives platform restart). */
  listRagIngestJobs: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ListRagIngestJobsResponse> => {
    const req = create(ListRequestSchema, {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 100,
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/ListRAGIngestJobs",
      toJson(ListRequestSchema, req),
    );
    const resp = fromJson(ListRAGIngestJobsResponseSchema, responseObject.data);
    const data = decodeValueData(
      resp.data,
    ) as Partial<ListRagIngestJobsResponse>;
    return { total: data.total ?? 0, jobs: data.jobs ?? [] };
  },

  /**
   * 删除一条构建历史：平台会先清 Qdrant 向量，再调 rag `DELETE .../rag-jobs/{ref}` 软删 PG 行。
   * `id` 可为 rag_job_id（如 jo-…）或 rag_ingest_jobs 主键 UUID（无 job 时的遗留行）。
   */
  deleteRagIngestJob: async (id: string): Promise<void> => {
    const req = create(DeleteRAGIngestJobRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/DeleteRAGIngestJob",
      toJson(DeleteRAGIngestJobRequestSchema, req),
    );
    fromJson(DeleteRAGIngestJobResponseSchema, responseObject.data);
  },

  /**
   * 重跑构建任务：先清 Qdrant 向量并软删旧记录，再按任务类型重新提交摄入。
   * 飞书任务须在 body 中传入 feishu_user_token 等凭证。
   */
  rerunRagIngestJob: async (
    id: string,
    body?: {
      feishu_user_token?: string;
      feishu_app_id?: string;
      feishu_app_secret?: string;
      feishu_dev_space?: string;
      recursive?: boolean;
      /** Fallback when upstream rag-jobs row is missing (service restart / soft-deleted). */
      name?: string;
      /** Fallback when upstream rag-jobs row is missing (service restart / soft-deleted). */
      qdrant_collection_name?: string;
    },
  ): Promise<RagRerunIngestJobResponse> => {
    const req = create(RerunRAGIngestJobRequestSchema, {
      id,
      feishuUserToken: body?.feishu_user_token ?? "",
      feishuAppId: body?.feishu_app_id ?? "",
      feishuAppSecret: body?.feishu_app_secret ?? "",
      feishuDevSpace: body?.feishu_dev_space ?? "",
      recursive: body?.recursive ?? false,
      name: body?.name ?? "",
      qdrantCollectionName: body?.qdrant_collection_name ?? "",
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/RerunRAGIngestJob",
      toJson(RerunRAGIngestJobRequestSchema, req),
    );
    const resp = fromJson(RerunRAGIngestJobResponseSchema, responseObject.data);
    return decodeValueData(resp.data) as unknown as RagRerunIngestJobResponse;
  },

  /** Soft-delete multiple rows (e.g. same rag task merged from multiple files). */
  deleteRagIngestJobsBatch: async (
    ids: string[],
  ): Promise<{ deleted: number }> => {
    const req = create(BatchDeleteRAGIngestJobsRequestSchema, { ids });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/BatchDeleteRAGIngestJobs",
      toJson(BatchDeleteRAGIngestJobsRequestSchema, req),
    );
    const resp = fromJson(
      BatchDeleteRAGIngestJobsResponseSchema,
      responseObject.data,
    );
    const data = decodeValueData(resp.data) as { deleted?: number };
    return { deleted: data.deleted ?? 0 };
  },

  /**
   * Multipart: field `files` (PDF / DOCX / Markdown), `qdrant_collection_name` (required), optional `timeout_seconds`.
   */
  uploadPdfs: async (params: {
    files: File[];
    qdrantCollectionName: string;
    timeoutSeconds?: number;
  }): Promise<RagUploadResponse> => {
    if (params.files.length > MAX_PDFS) {
      throw new Error(`最多上传 ${MAX_PDFS} 个文件`);
    }
    const formData = new FormData();
    formData.set("qdrant_collection_name", params.qdrantCollectionName.trim());
    if (params.timeoutSeconds !== undefined) {
      formData.set("timeout_seconds", String(params.timeoutSeconds));
    }
    for (const f of params.files) {
      formData.append("files", f);
    }
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/UploadRAGPDFs",
      formData,
      {
        timeout: 30 * 60 * 1000,
      },
    );
    const resp = fromJson(UploadRAGPDFsResponseSchema, responseObject.data);
    const data = decodeValueData(resp.data) as Partial<RagUploadResponse>;
    return { results: data.results ?? [] };
  },

  uploadFeishuDocument: async (params: {
    feishuDocumentId: string;
    qdrantCollectionName: string;
    recursive?: boolean;
    name?: string;
    feishuUserToken: string;
    feishuAppId?: string;
    feishuAppSecret?: string;
    feishuDevSpace: string;
  }): Promise<RagFeishuUploadResponse> => {
    const req = create(UploadRAGFeishuRequestSchema, {
      feishuDocumentId: params.feishuDocumentId.trim(),
      qdrantCollectionName: params.qdrantCollectionName.trim(),
      recursive: params.recursive ?? false,
      name: params.name?.trim() ?? "",
      feishuUserToken: params.feishuUserToken.trim(),
      feishuAppId: params.feishuAppId?.trim() ?? "",
      feishuAppSecret: params.feishuAppSecret?.trim() ?? "",
      feishuDevSpace: params.feishuDevSpace.trim(),
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/UploadRAGFeishu",
      toJson(UploadRAGFeishuRequestSchema, req),
    );
    const resp = fromJson(UploadRAGFeishuResponseSchema, responseObject.data);
    return decodeValueData(resp.data) as unknown as RagFeishuUploadResponse;
  },

  uploadWebUrls: async (params: {
    urls: string[];
    qdrantCollectionName: string;
    sitemapUrl?: string;
    autoRerunIntervalHours?: number;
  }): Promise<RagWebUploadResponse> => {
    const req = create(UploadRAGWebRequestSchema, {
      urls: params.urls.map((u) => u.trim()).filter((u) => u.length > 0),
      qdrantCollectionName: params.qdrantCollectionName.trim(),
      sitemapUrl: params.sitemapUrl?.trim() ?? "",
      autoRerunIntervalHours: params.autoRerunIntervalHours ?? 0,
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/UploadRAGWeb",
      toJson(UploadRAGWebRequestSchema, req),
    );
    const resp = fromJson(UploadRAGWebResponseSchema, responseObject.data);
    return decodeValueData(resp.data) as unknown as RagWebUploadResponse;
  },

  /** Parse a sitemap.xml URL and return extracted page URLs. */
  parseSitemap: async (params: {
    url: string;
  }): Promise<SitemapParseResponse> => {
    const req = create(ParseRAGSitemapRequestSchema, {
      url: params.url.trim(),
    });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/ParseRAGSitemap",
      toJson(ParseRAGSitemapRequestSchema, req),
    );
    const resp = fromJson(ParseRAGSitemapResponseSchema, responseObject.data);
    return decodeValueData(resp.data) as unknown as SitemapParseResponse;
  },

  /** Initiate Feishu OAuth: POST credentials, get authorize URL, then browser redirects. */
  initiateFeishuAuth: async (params: {
    token: string;
    appId: string;
    appSecret: string;
    redirectUri: string;
  }): Promise<{ authorize_url: string }> => {
    const response = await apiClient.post<{ authorize_url: string }>(
      "/public/rag/feishu/auth",
      {
        token: params.token,
        app_id: params.appId.trim(),
        app_secret: params.appSecret.trim(),
        redirect_uri: params.redirectUri.trim(),
      },
    );
    return response.data;
  },

  /** Exchange the one-time encrypted code for the real feishu user_access_token. */
  exchangeFeishuToken: async (params: {
    code: string;
    appSecret: string;
  }): Promise<{ feishu_user_token: string; expires_in: number }> => {
    const response = await apiClient.post<{
      feishu_user_token: string;
      expires_in: number;
    }>("/public/rag/feishu/token", {
      code: params.code,
      app_secret: params.appSecret,
    });
    return response.data;
  },

  getJob: async (jobId: string): Promise<RagJobStatusResponse> => {
    const req = create(GetRAGIngestJobRequestSchema, { jobId });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/GetRAGIngestJob",
      toJson(GetRAGIngestJobRequestSchema, req),
    );
    const resp = fromJson(GetRAGIngestJobResponseSchema, responseObject.data);
    return decodeValueData(resp.data) as unknown as RagJobStatusResponse;
  },

  getJobLogs: async (jobId: string): Promise<RagJobLogsResponse> => {
    const req = create(GetRAGIngestJobLogsRequestSchema, { jobId });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/GetRAGIngestJobLogs",
      toJson(GetRAGIngestJobLogsRequestSchema, req),
    );
    const resp = fromJson(
      GetRAGIngestJobLogsResponseSchema,
      responseObject.data,
    );
    return decodeValueData(resp.data) as unknown as RagJobLogsResponse;
  },

  /**
   * Proxied POST text/plain stream from rag (stdout/stderr until job ends).
   * Uses fetch + ReadableStream so chunks arrive progressively (raw chunked, no v2 envelope).
   */
  streamJobLogs: async (params: {
    jobId: string;
    signal?: AbortSignal;
    onChunk: (text: string) => void;
    onComplete?: () => void;
    onError?: (err: Error) => void;
  }): Promise<void> => {
    const url = absoluteApiUrl("/v2/GetRAGIngestJobLogsStream");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getBearerToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const res = await fetchWithApiMock("api", url, {
        method: "POST",
        headers,
        body: JSON.stringify({ job_id: params.jobId }),
        signal: params.signal,
        cache: "no-store",
      });
      if (!res.ok) {
        if (res.status === 404) {
          params.onComplete?.();
          return;
        }
        const t = await res.text().catch(() => res.statusText);
        throw new Error(t || `HTTP ${res.status}`);
      }
      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error("响应不支持流式读取");
      }
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          const raw = dec.decode(value, { stream: true });
          if (raw.length > 0) {
            params.onChunk(raw);
          }
        }
      }
      const tail = dec.decode();
      if (tail.length > 0) {
        params.onChunk(tail);
      }
      params.onComplete?.();
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        return;
      }
      params.onError?.(e instanceof Error ? e : new Error(String(e)));
    }
  },

  /** List RAG_RERUN cron jobs for the current tenant. */
  listRerunJobs: async (): Promise<ListRagRerunJobsResponse> => {
    const req = create(ListRequestSchema, { page: 1, pageSize: 100 });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/ListRAGRerunJobs",
      toJson(ListRequestSchema, req),
    );
    const resp = fromJson(ListRAGRerunJobsResponseSchema, responseObject.data);
    const data = decodeValueData(
      resp.data,
    ) as Partial<ListRagRerunJobsResponse>;
    return { total: data.total ?? 0, jobs: data.jobs ?? [] };
  },

  /** Delete (stop) a RAG_RERUN cron job. */
  deleteRerunJob: async (
    id: string,
  ): Promise<{ deleted: boolean; job_id: string }> => {
    const req = create(DeleteRAGRerunJobRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      "/v2/DeleteRAGRerunJob",
      toJson(DeleteRAGRerunJobRequestSchema, req),
    );
    const resp = fromJson(DeleteRAGRerunJobResponseSchema, responseObject.data);
    const data = decodeValueData(resp.data) as {
      deleted?: boolean;
      job_id?: string;
    };
    return { deleted: data.deleted ?? false, job_id: data.job_id ?? id };
  },
};
